import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { firstAllowedPath } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";

function formatApiError(detail) {
  if (detail == null) return "Algo deu errado. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const userData = await login(username, password);
      toast.success("Login realizado com sucesso!");
      navigate(firstAllowedPath(userData));
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1683830990630-06ed2a68c842?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwyfHxzdGVlbCUyMHdlbGRpbmclMjBzcGFya3MlMjBjb25zdHJ1Y3Rpb258ZW58MHx8fHwxNzc1NTAwMTk4fDA&ixlib=rb-4.1.0&q=85"
          alt="Soldagem industrial"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[hsl(222,80%,25%)] to-black/60 mix-blend-multiply" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <img src="/logo-liga-facil.png" alt="Liga Facil" className="h-24 w-auto mb-6 drop-shadow-lg" />
          <p className="text-white/80 text-base leading-relaxed max-w-md">
            Sistema de Planejamento, Controle e Producao para fabricacao de kits eletricos e hidraulicos.
            Acompanhe cada etapa do seu processo produtivo em tempo real.
          </p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <Card className="w-full max-w-md border border-border shadow-none">
          <CardHeader className="space-y-1">
            <div className="flex items-center gap-2 mb-2">
              <img src="/logo-liga-facil.png" alt="Liga Facil" className="h-14 w-auto" />
            </div>
            <CardTitle className="font-heading text-2xl tracking-tight">Entrar</CardTitle>
            <CardDescription>Acesse o sistema com suas credenciais</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="login-error">
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="username">Usuario</Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="Seu usuario"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  data-testid="login-username-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Senha</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Sua senha"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? "Entrando..." : "Entrar"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
